import { useLingui } from '@lingui/react/macro';

import { FeeTypeSlug, PriceAmount } from '@/api';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconInfo from '@/icons/Info.svg';
import { isArchived, useGetArticleName } from '@/models/article';
import { useWorkshop } from '@/models/workshop';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { formatCurrency } from '@/utils/number';
import { getFeesGroupedByType, shouldShowPendingWorkshopQuote } from '@/utils/price';

import {
  PendingQuoteBadge,
  PriceWithDetailsTooltipWithConversions,
} from './PriceWithDetails/PriceWithDetails';
import { FEE_TYPE_LABELS } from './PriceWithDetails/usePriceDetailsRows';
import WorkshopPriceLabel from './WorkshopPriceLabel/WorkshopPriceLabel';

export const RequestWorkshopPrice = () => {
  const { request } = useRequestContext();

  const workshopIds = (request.workshopPrice?.amountPerEntity
    ?.map(({ entityId }) => entityId)
    .filter((workshopIdOrNull) => !!workshopIdOrNull) ?? []) as string[];

  if (!workshopIds.length) {
    return <RequestWorkshopPriceElement workshopId={undefined} showWorkshopName={false} />;
  }

  return (
    <Stack gap="1rem" style={{ flex: 1 }}>
      {workshopIds.map((workshopId) => (
        <RequestWorkshopPriceElement
          key={workshopId}
          workshopId={workshopId}
          showWorkshopName={workshopIds.length > 1}
        />
      ))}
    </Stack>
  );
};

const RequestWorkshopPriceElement = ({
  workshopId,
  showWorkshopName,
}: {
  workshopId: string | undefined;
  showWorkshopName: boolean;
}) => {
  const { request, view: requestView, iam } = useRequestContext();
  const { t } = useLingui();

  const workshop = useWorkshopFromRequestOrGet(workshopId);

  const total = workshopId
    ? request.workshopPrice?.amountPerEntity?.find(({ entityId }) => entityId === workshopId)
    : request.workshopPrice?.amountPerCurrency?.[0];
  const fees = getFeesGroupedByType(request, 'workshop', workshopId);

  const showTotalLine =
    !!total ||
    request.allArticles.some(
      (article) =>
        (workshopId ? article.workshopId === workshopId : true) &&
        !isArchived(article) &&
        (article.snapshot.serviceChoice === 'care-repair' ||
          article.snapshot.serviceChoice === 'exchange')
    );

  if (fees.length === 0 && !showTotalLine) {
    return null;
  }

  const showAmountBeforeTaxes = requestView.workshopPrice.showAmountBeforeTaxes;
  const label = iam.hasOnlyWorkshopScopeOnRequest
    ? t({
        id: 'request-context.total-cost.workshop',
        message: 'Total price',
      })
    : t({
        id: 'request-context.total-cost',
        message: 'Total internal cost',
      });
  const ariaLabel = showWorkshopName && workshop ? `${label} - ${workshop.name}` : label;

  const showPendingQuote = request.articles.some(
    (article) =>
      (workshopId ? article.workshopId === workshopId : true) &&
      shouldShowPendingWorkshopQuote(article)
  );

  return (
    <Stack
      ariaLabel={ariaLabel}
      className="bg-neutral-200"
      gap="0.25rem"
      padding="0.75rem"
      style={{ borderRadius: '0.5rem', flex: 1 }}
    >
      <RequestWorkshopPriceFees workshopId={workshopId} />
      {showTotalLine && (
        <Stack
          row
          justifyContent="space-between"
          alignItems="flex-start"
          gap="1rem"
          flexWrap="nowrap"
        >
          <WorkshopPriceLabel
            noVATApplied={total?.noVATApplied}
            workshopName={showWorkshopName && workshop ? workshop.name : undefined}
            isTotalLabel
            color="primary"
          />
          {total ? (
            <PriceWithDetailsTooltipWithConversions
              price={total}
              direction="row-reverse"
              options={{
                showAmountBeforeTaxes: showAmountBeforeTaxes,
              }}
              variant="primary"
            />
          ) : showPendingQuote ? (
            <PendingQuoteBadge />
          ) : (
            <>-</>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const RequestWorkshopPriceFees = ({ workshopId }: { workshopId: string | undefined }) => {
  const { i18n } = useLingui();
  const { request, view: requestView } = useRequestContext();

  const fees = getFeesGroupedByType(request, 'workshop', workshopId);

  return (
    <>
      {fees.map((fee) => {
        const feeLabel = i18n._(FEE_TYPE_LABELS[fee.type]);

        return (
          <Stack
            key={fee.type}
            row
            justifyContent="space-between"
            alignItems="flex-start"
            gap="1rem"
            flexWrap="nowrap"
            ariaLabel={feeLabel}
          >
            <Stack row alignItems="center" style={{ columnGap: '0.25rem', rowGap: 0 }}>
              <InlineText color="secondary">{feeLabel}</InlineText>
              <Stack row alignItems="center" gap="0.25rem" flexWrap="nowrap">
                <WorkshopPriceLabel
                  hideLabel
                  noVATApplied={fee.price.noVATApplied}
                  color="secondary"
                />
                {(fee.type === 'cancellation' ||
                  fee.type === 'shipping-management' ||
                  fee.type === 'paid-estimate-advance') && (
                  <FeeDetailsTooltip type={fee.type} workshopId={workshopId} />
                )}
              </Stack>
            </Stack>
            <PriceWithDetailsTooltipWithConversions
              price={fee.price}
              direction="row-reverse"
              options={{
                showAmountBeforeTaxes: requestView.workshopPrice.showAmountBeforeTaxes,
              }}
              variant="secondary"
            />
          </Stack>
        );
      })}
    </>
  );
};

const FeeDetailsTooltip = ({
  type,
  workshopId,
}: {
  type: FeeTypeSlug;
  workshopId: string | undefined;
}) => {
  const { request } = useRequestContext();
  const getArticleName = useGetArticleName();

  const feesPerArticle = request.allArticles
    .map((article) => {
      return {
        articleId: article.id,
        articleName: getArticleName({ article }),
        fees: article.snapshot.fees
          .filter((fee) => fee.feeType.slug === type)
          .map((fee) => {
            const price = workshopId
              ? fee?.workshopPrice?.amountPerEntity?.find(({ entityId }) => entityId === workshopId)
              : fee?.workshopPrice?.amountPerEntity?.[0];

            return {
              price,
              workshopId: price?.entityId,
            };
          })
          .filter(({ price, workshopId }) => !!price && !!workshopId),
      };
    })
    .filter(({ fees }) => !!fees.length);

  if (!feesPerArticle.length) {
    return null;
  }

  return (
    <Tooltip
      content={
        <Stack gap="0.25rem">
          {feesPerArticle.map(({ articleId, articleName, fees }) => (
            <Stack key={articleId}>
              <Paragraph size="200" color="disabled">
                {articleName}
              </Paragraph>
              {fees.map((fee) => (
                <FeeDetailsTooltipWorkshopPrice
                  key={fee.workshopId}
                  workshopId={fee.workshopId!}
                  price={fee.price!}
                />
              ))}
            </Stack>
          ))}
        </Stack>
      }
      textAlign="start"
    >
      <Button variant="style-less">
        <IconInfo style={{ fontSize: '1rem' }} className="text-disabled" />
      </Button>
    </Tooltip>
  );
};

const FeeDetailsTooltipWorkshopPrice = ({
  workshopId,
  price,
}: {
  workshopId: string;
  price: PriceAmount;
}) => {
  const { view: requestView } = useRequestContext();
  const workshop = useWorkshopFromRequestOrGet(workshopId);

  if (!workshop) {
    return null;
  }

  return (
    <Paragraph size="200" color="neutral">
      • {workshop.name}:{' '}
      {formatCurrency(
        requestView.workshopPrice.showAmountBeforeTaxes ? price.amountBeforeTaxes : price.amount,
        price.currency
      )}
    </Paragraph>
  );
};

const useWorkshopFromRequestOrGet = (workshopId?: string) => {
  const { request } = useRequestContext();

  const alreadyFetchedWorkshop = workshopId
    ? request.allArticles
        .map((article) => article.workshop)
        .find((entity) => entity?.id === workshopId)
    : null;
  const { data: fetchedWorkshop } = useWorkshop(workshopId, {
    enabled: !alreadyFetchedWorkshop && !!workshopId,
  });
  const workshop = alreadyFetchedWorkshop ?? fetchedWorkshop;

  if (!workshopId) {
    return null;
  }

  return workshop;
};
